import { Check, Minus } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { Seo } from "../lib/head";
import { APP_URL } from "../lib/env";

const TIERS: Array<{
  name: string;
  tagline: string;
  price: string;
  unit: string;
  facilities: string;
  highlight?: boolean;
  bullets: string[];
  cta: string;
  to?: string;
  href?: string;
}> = [
  {
    name: "Starter",
    tagline: "For solo consultant pharmacists.",
    price: "$149",
    unit: "/ facility / mo",
    facilities: "1–3 facilities",
    bullets: [
      "All clinical rule packs",
      "Prescriber + DON e-signature",
      "QuickBooks invoicing",
      "Email reminders",
      "99.5% uptime SLA",
      "BAA signed",
    ],
    cta: "Start free trial",
    href: APP_URL,
  },
  {
    name: "Professional",
    tagline: "For multi-pharmacist firms.",
    price: "$199",
    unit: "/ facility / mo",
    facilities: "4–25 facilities · volume pricing",
    highlight: true,
    bullets: [
      "Everything in Starter",
      "AI synthesis + learning loop",
      "SMS reminders",
      "Calendar sync (Google + Outlook)",
      "Assessment worksheets",
      "State board filings",
      "99.9% uptime SLA",
    ],
    cta: "Start free trial",
    href: APP_URL,
  },
  {
    name: "Enterprise",
    tagline: "For operators, health systems & MSOs.",
    price: "Custom",
    unit: "",
    facilities: "25+ facilities",
    bullets: [
      "Everything in Professional",
      "Custom rule pack development",
      "SSO (SAML / OIDC)",
      "Dedicated success manager",
      "Custom retention windows",
      "99.99% uptime SLA",
    ],
    cta: "Talk to sales",
    to: "/demo",
  },
];

const COMPARISON: Array<{ label: string; tiers: [boolean, boolean, boolean] | [string, string, string] }> = [
  { label: "Clinical rule packs", tiers: [true, true, true] },
  { label: "Adobe Sign workflows", tiers: [true, true, true] },
  { label: "QBO invoicing", tiers: [true, true, true] },
  { label: "Email reminders", tiers: [true, true, true] },
  { label: "AI synthesis", tiers: [false, true, true] },
  { label: "SMS reminders", tiers: [false, true, true] },
  { label: "Calendar sync", tiers: [false, true, true] },
  { label: "Assessment worksheets", tiers: [false, true, true] },
  { label: "State board filings", tiers: [false, true, true] },
  { label: "SSO (SAML / OIDC)", tiers: [false, false, true] },
  { label: "Custom rule development", tiers: [false, false, true] },
  { label: "Uptime SLA", tiers: ["99.5%", "99.9%", "99.99%"] },
];

export function Pricing() {
  return (
    <>
      <Seo title="Consultant Pharmacist Software Pricing | WeConsult Rx" description="Simple per-facility pricing for consultant pharmacist software: Starter from $149/facility/mo, Professional $199, Enterprise custom. Every tier includes a BAA and HIPAA controls." path="/pricing" />
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Pricing</Eyebrow>
            <Heading level={1} className="mt-3">
              Simple per-facility pricing.
            </Heading>
            <Lede className="mt-6">
              Pay for the facilities you cover — a fraction of what you bill each one. Every tier ships with
              HIPAA controls and a BAA, no upsell tax for compliance.{" "}
              <a href="/roi-calculator" className="font-semibold text-forest-700 underline hover:text-forest-900">
                See your ROI
              </a>
              .
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-8">
        <Container>
          <div className="grid gap-6 lg:grid-cols-3">
            {TIERS.map((t) => (
              <Card
                key={t.name}
                className={`relative h-full ${
                  t.highlight ? "border-forest-700 ring-1 ring-forest-700" : ""
                }`}
              >
                {t.highlight ? (
                  <Badge className="absolute -top-3 left-6">Most popular</Badge>
                ) : null}
                <Heading level={3}>{t.name}</Heading>
                <p className="mt-2 text-ink-600">{t.tagline}</p>
                <div className="mt-6">
                  <span className="text-4xl font-extrabold tracking-tightest">{t.price}</span>
                  {t.unit ? <span className="text-sm text-ink-500"> {t.unit}</span> : null}
                  <p className="mt-1 text-sm font-medium text-ink-500">{t.facilities}</p>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-ink-700">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-forest-700 mt-0.5" aria-hidden />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  {t.to ? (
                    <LinkButton to={t.to} variant={t.highlight ? "primary" : "secondary"} className="w-full justify-center">
                      {t.cta}
                    </LinkButton>
                  ) : (
                    <LinkButton href={t.href ?? APP_URL} variant={t.highlight ? "primary" : "secondary"} className="w-full justify-center">
                      {t.cta}
                    </LinkButton>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Feature comparison</Heading>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-5 py-3 font-semibold text-ink-700 w-1/2">Feature</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center">Starter</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center bg-forest-50">Professional</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-canvas/50" : ""}>
                    <td className="px-5 py-3 text-ink-800">{row.label}</td>
                    {row.tiers.map((t, idx) => (
                      <td
                        key={idx}
                        className={`px-5 py-3 text-center ${idx === 1 ? "bg-forest-50/50" : ""}`}
                      >
                        {typeof t === "boolean" ? (
                          t ? (
                            <Check className="h-4 w-4 text-forest-700 mx-auto" aria-label="Yes" />
                          ) : (
                            <Minus className="h-4 w-4 text-ink-300 mx-auto" aria-label="No" />
                          )
                        ) : (
                          <span className="text-ink-800 font-semibold">{t}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>

      <Section className="text-center">
        <Container>
          <Heading level={2}>Ready to talk?</Heading>
          <p className="mt-4 text-ink-600 max-w-2xl mx-auto">
            Book a 30-minute call. Bring a sample chart. We’ll walk you through
            it live.
          </p>
          <div className="mt-8">
            <LinkButton to="/demo" size="lg">
              Request a demo
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}
